/**
 * The pipeline's rulebook: when may a piece of work run?
 *
 * Every condition is a pure function of the exam — no Mongo, no other collection, no throwing — so this file can
 * be read top to bottom as the answer to "what is this exam waiting for?" and exercised without a database. That
 * is the whole point of deriving readiness instead of storing it: the exam state stays the single source of truth,
 * and a gate opens by the domain write that opens it (a confirmation flag flipping), not by a second write into
 * the job collection.
 *
 * THESE ARE PLAIN EXPORTED FUNCTIONS, composed inside a definition's `ready()`. They used to be values in a
 * `CONDITIONS` map addressed by string key, so that a job ROW could carry extra gates (`requires` / `dropIf`)
 * in Mongo. That indirection is gone, and it is worth recording why, because it looked principled:
 *
 *  - Storing keys rather than functions was the right call GIVEN that rows carried gates — a stored function is
 *    code executed out of a database record. But rows never needed to carry gates in the first place. A gate is a
 *    property of a job TYPE, and `ready(exam, job)` already receives the job, so anything a row could have
 *    expressed via `requires` it can express by branching on `job.payload` — in typechecked code.
 *  - The string indirection then cost real safety rather than buying it: a key had to be validated at enqueue
 *    time, could go stale across a rename, and needed a three-valued `holds()` returning `'unknown'` so that a
 *    renamed key made a job wait rather than silently evaluate false. All of that was machinery guarding against
 *    a failure mode that only existed because the keys were strings.
 *
 * So: one place decides readiness — the definition's own `ready()` — and the compiler checks it.
 */

import { logger } from '@adornis/base/logging.js';
import type { Exam } from '../../exam.js';
import { StepState } from '../../extraction-progress.js';
import { isExamCreatorLocked } from '../../utils/exam-lock.js';
import { isGeneratedExam } from '../../utils/exam-origin.js';
import { anySetupStepOngoing } from '../pending-tasks/extraction-or-generation-finished.js';
import { isStructureConfirmed } from '../tools/file-assignment.js';
import type { JobDefinition, JobDoc, JobPayload, JobVerdict, ReadyResult } from './job.js';

/**
 * What a condition is allowed to assume it got: a possibly-incomplete exam document. Deliberately not `Exam` — a
 * condition must stay total, and a projected or half-migrated document is exactly the case where an optimistic
 * field access would throw inside the runner's decision loop.
 */
export type ConditionExam = Partial<Exam> | null | undefined;

/** The teacher signed off the task tree — the precondition for matching anything against it. */
export const structureConfirmed = (exam: ConditionExam): boolean => isStructureConfirmed(exam?.examState);

/** This exam's task tree was authored by the exam-creation tool, not uploaded — see `isGeneratedExam`. */
export const generatedExam = (exam: ConditionExam): boolean => isGeneratedExam(exam);

/** The exam-creator (job and canvas alike) is locked for this exam — see `isExamCreatorLocked`. */
export const examCreatorLocked = (exam: ConditionExam): boolean => isExamCreatorLocked(exam);

export const hasMasterSolutionFile = (exam: ConditionExam): boolean => (exam?.masterSolutionIds?.length ?? 0) > 0;
export const hasGradingSchemeFile = (exam: ConditionExam): boolean => (exam?.gradingSchemeIds?.length ?? 0) > 0;
export const hasBlankExam = (exam: ConditionExam): boolean => (exam?.blankExamIds?.length ?? 0) > 0;

/**
 * An UPLOAD of this artifact is being read right now. Extraction and generation are two producers of the
 * SAME field, so they are the one pair that narrowing the writes cannot separate — the generation has to wait
 * (and will usually then find its roots already covered and drop).
 *
 * Lives here rather than in the two job modules so that both ask the question the same way, and so the
 * optional chains stay on `Partial<Exam>` where they are load-bearing rather than on a document type that
 * merely claims the fields are always present.
 */
export const artifactExtractionRunning = (
  exam: ConditionExam,
  artifact: 'masterSolution' | 'gradingScheme',
): boolean => {
  const progress =
    artifact === 'masterSolution' ? exam?.examState?.masterSolutionProgress : exam?.examState?.gradingSchemeProgress;
  return progress?.producer === 'extraction' && progress.status === StepState.IN_PROGRESS;
};

export const hasTasks = (exam: ConditionExam): boolean => Array.isArray(exam?.tasks) && exam.tasks.length > 0;

/** Upload progress found at least one submission. */
export const hasSubmissions = (exam: ConditionExam): boolean =>
  (exam?.examState?.uploadProgress?.submissionsFound ?? 0) > 0;

/** The split has nothing left to wait on: never requested for this exam, or the teacher has confirmed it. */
export const splitConfirmedOrNotApplicable = (exam: ConditionExam): boolean => {
  const splitState = exam?.examState?.splitConfirmationState;
  return !splitState || splitState.confirmed === true;
};

/**
 * How long a `settle` row waits for the exam to stop changing before it runs anyway.
 *
 * It cannot be open-ended, and the reason is the split confirmation: `anySetupStepOngoing` counts it as running
 * and it waits on a TEACHER. Confirmed inside the window → everything is graded and announced together; left
 * sitting → whatever is ready is graded now and whatever confirms later announces for itself.
 */
export const SETTLE_WINDOW_MS = 10 * 60_000;

/**
 * A background re-grade holds off while any setup step of the exam is still in flight, so one upload that
 * changes three things is graded once rather than three times — bounded by the row's own age.
 *
 * SHARED BY EVERY PHASE OF THE GRADING WAVE. `grading` and `languageUseGroupGrading` are both dispatched with
 * the same scope and must therefore wait out the same window; having each spell the condition out invited them
 * to drift, and made one type module import a constant from another.
 */
export const settling = (exam: ConditionExam, job: { payload: { settle?: boolean }; createdAt: Date }): boolean =>
  !!job.payload.settle &&
  !!exam?.examState &&
  anySetupStepOngoing(exam.examState) &&
  Date.now() - new Date(job.createdAt).getTime() < SETTLE_WINDOW_MS;

const VERDICTS = new Set<JobVerdict>(['run', 'wait', 'drop']);

/**
 * The effective verdict for one job: the definition's own `ready()`, or `run` when it has none.
 *
 * `ready()` is allowed to be wrong in exactly one way that this has to absorb — returning something that is not a
 * verdict. That must not be read as "not run", because a typo would then become an eternal, silent wait. It is
 * held back loudly instead, the same treatment a throwing `ready()` gets and for the same reason: better to hold
 * work than to run it on a precondition nobody managed to evaluate.
 */
export async function evaluateReadiness<P extends JobPayload>(
  definition: JobDefinition<P>,
  exam: Exam,
  job: JobDoc<P>,
): Promise<ReadyResult> {
  if (!definition.ready) return { verdict: 'run' };

  let own: JobVerdict | ReadyResult;
  try {
    // AWAITED, so a gate may be a query. A rejected promise lands in the same catch as a throw and gets the same
    // treatment — held back loudly — because "the precondition could not be evaluated" is one situation, not two.
    own = await definition.ready(exam, job);
  } catch (err) {
    logger.error({ err, jobId: job._id, type: job.type }, 'Job ready() threw — holding the job back');
    return { verdict: 'wait', because: `${definition.type}.ready:threw` };
  }

  const result: ReadyResult = typeof own === 'string' ? { verdict: own, because: `${definition.type}.ready` } : own;

  if (!VERDICTS.has(result?.verdict)) {
    logger.error(
      { jobId: job._id, type: job.type, returned: result?.verdict },
      'Job ready() returned an unrecognised verdict — holding the job back',
    );
    return { verdict: 'wait', because: `${definition.type}.ready:invalid-verdict` };
  }

  return result;
}
